import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
} from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { DateTime } from 'luxon';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  getMappingBlockers,
  type WizardAction,
  type WizardState,
} from '@/components/campaign-import/import-wizard.reducer';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  IMPORT_FIELDS,
  REQUIRED_IMPORT_FIELDS,
  type ImportField,
} from '@/lib/campaign-import/column-mapping';
import {
  DATE_FORMATS,
  detectDateFormat,
  type DateFormatId,
} from '@/lib/campaign-import/date-parse';
import { cn } from '@/lib/utils';

const SAMPLE_SIZE = 3;
const STAGGER_SECONDS = 0.06;
const NO_COLUMN = 'none';
const TIME_LIKE = /^\d{1,2}:\d{2}(:\d{2})?$/;

type ColumnType = 'date' | 'dateTime' | 'time' | 'link' | 'text';

function isLink(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/** Kind of content a column holds, read from its sample (only when every value agrees). */
function inferColumnType(samples: string[]): ColumnType {
  if (samples.length === 0) return 'text';
  if (samples.every(isLink)) return 'link';
  if (samples.every((value) => TIME_LIKE.test(value))) return 'time';
  if (detectDateFormat(samples, false) && allParse(samples, false)) {
    return 'dateTime';
  }
  if (detectDateFormat(samples, true) && allParse(samples, true)) {
    return 'date';
  }
  return 'text';
}

function allParse(samples: string[], hasTimeColumn: boolean): boolean {
  const format = detectDateFormat(samples, hasTimeColumn);
  if (!format) return false;
  return samples.every((sample) => detectDateFormat([sample], hasTimeColumn));
}

type FieldStatus = 'mapped' | 'suggested' | 'missing' | 'unused';

interface Connection {
  field: ImportField;
  path: string;
}

function isRequired(field: ImportField): boolean {
  return (REQUIRED_IMPORT_FIELDS as readonly ImportField[]).includes(field);
}

export interface MappingStepProps {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
}

function useMappingCopy() {
  const { LL } = useI18nContext();
  return {
    fieldLabels: {
      name: LL.campaignImport.mapping.fields.name(),
      launchDate: LL.campaignImport.mapping.fields.launchDate(),
      launchTime: LL.campaignImport.mapping.fields.launchTime(),
      notificationTitle: LL.campaignImport.mapping.fields.notificationTitle(),
      notificationBody: LL.campaignImport.mapping.fields.notificationBody(),
      notificationLink: LL.campaignImport.mapping.fields.notificationLink(),
      utmCampaign: LL.campaignImport.mapping.fields.utmCampaign(),
      labels: LL.campaignImport.mapping.fields.labels(),
    } satisfies Record<ImportField, string>,
    columnTypeLabels: {
      date: LL.campaignImport.mapping.columnTypes.date(),
      dateTime: LL.campaignImport.mapping.columnTypes.dateTime(),
      time: LL.campaignImport.mapping.columnTypes.time(),
      link: LL.campaignImport.mapping.columnTypes.link(),
      text: LL.campaignImport.mapping.columnTypes.text(),
    } satisfies Record<ColumnType, string>,
    statusLabels: {
      mapped: LL.campaignImport.mapping.status.mapped(),
      suggested: LL.campaignImport.mapping.status.suggested(),
      missing: LL.campaignImport.mapping.status.missing(),
      unused: LL.campaignImport.mapping.status.unused(),
    } satisfies Record<FieldStatus, string>,
  };
}

const STATUS_STYLES: Record<FieldStatus, string> = {
  mapped: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
  suggested: 'border-[#155DFC]/60 bg-[#155DFC]/20 text-[#B7CCFF]',
  missing: 'border-amber-300/50 bg-amber-300/10 text-amber-200',
  unused: 'border-white/15 bg-white/5 text-white/55',
};

function TimezoneCombobox({
  timezone,
  onChange,
}: {
  timezone: string;
  onChange: (timezone: string) => void;
}) {
  const { LL } = useI18nContext();
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => Intl.supportedValuesOf('timeZone'), []);

  return (
    <div className="space-y-2">
      <Label htmlFor="import-timezone" className="text-white/85">
        {LL.createCampaign.timezoneLabel()}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="import-timezone"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-white/20 bg-white/5 font-normal text-white hover:bg-white/10 hover:text-white"
          >
            {timezone}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="relative w-[--radix-popover-trigger-width] overflow-hidden p-0">
          <Command>
            <CommandInput className="sticky top-0" />
            <CommandEmpty>
              {LL.campaignImport.mapping.timezoneEmpty()}
            </CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-y-scroll">
              {zones.map((zone) => (
                <CommandItem
                  key={zone}
                  value={zone}
                  onSelect={(current) => {
                    setOpen(false);
                    onChange(current);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      timezone === zone ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {zone}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function MappingStep({ state, dispatch }: MappingStepProps) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion();
  const { fieldLabels, columnTypeLabels, statusLabels } = useMappingCopy();
  const file = state.file;

  const containerRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef(new Map<number, HTMLElement>());
  const fieldRefs = useRef(new Map<ImportField, HTMLElement>());
  const [connections, setConnections] = useState<Connection[]>([]);
  const [gutter, setGutter] = useState({ width: 0, height: 0 });

  const columns = useMemo(
    () =>
      (file?.headers ?? []).map((header, index) => {
        const values = (file?.rows ?? [])
          .map((row) => (row.cells[index] ?? '').trim())
          .filter((value) => value !== '');
        const samples = values.slice(0, SAMPLE_SIZE);
        return { header, index, samples, type: inferColumnType(samples) };
      }),
    [file]
  );

  const blockers = getMappingBlockers(state);
  const missingFields = blockers.flatMap((blocker) =>
    blocker.type === 'missingFields' ? blocker.fields : []
  );
  const hasTimeColumn = state.mapping.launchTime !== undefined;
  const formatOptions: DateFormatId[] = DATE_FORMATS.filter(
    (format) =>
      format === state.dateFormat ||
      (format === 'iso' || format.includes('HH:mm')) !== hasTimeColumn
  );

  const statusOf = (field: ImportField): FieldStatus => {
    const column = state.mapping[field];
    if (column === undefined) return isRequired(field) ? 'missing' : 'unused';
    const suggestion = state.suggestions.find((item) => item.field === field);
    return suggestion?.column === column ? 'suggested' : 'mapped';
  };

  const mappingSignature = JSON.stringify(state.mapping);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const box = container.getBoundingClientRect();
      const next: Connection[] = [];
      for (const field of IMPORT_FIELDS) {
        const column = state.mapping[field];
        if (column === undefined) continue;
        const from = columnRefs.current.get(column)?.getBoundingClientRect();
        const to = fieldRefs.current.get(field)?.getBoundingClientRect();
        if (!from || !to) continue;
        const startY = from.top + from.height / 2 - box.top;
        const endY = to.top + to.height / 2 - box.top;
        next.push({
          field,
          path: `M 0 ${startY} C 40 ${startY}, 40 ${endY}, 80 ${endY}`,
        });
      }
      setConnections(next);
      setGutter({ width: 80, height: box.height });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // `mappingSignature` stands for `state.mapping` by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingSignature, columns]);

  if (!file) return null;

  return (
    <div className="space-y-8">
      <div
        ref={containerRef}
        className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_80px_minmax(0,1fr)] lg:gap-0"
      >
        <section
          aria-labelledby="mapping-columns-title"
          className="space-y-3 lg:col-start-1 lg:row-start-1"
        >
          <h3
            id="mapping-columns-title"
            className="text-sm font-semibold text-white/85"
          >
            {LL.campaignImport.mapping.columnsTitle()}
          </h3>
          <ul className="space-y-2">
            {columns.map((column, position) => (
              <motion.li
                key={column.index}
                ref={(node: HTMLLIElement | null) => {
                  if (node) columnRefs.current.set(column.index, node);
                  else columnRefs.current.delete(column.index);
                }}
                data-column={column.index}
                initial={reduceMotion ? false : { opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: reduceMotion ? 0 : position * STAGGER_SECONDS,
                }}
                className="rounded-lg border border-white/10 bg-white/[0.04] px-3.5 py-2.5"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-sm font-medium">
                    {column.header}
                  </span>
                  <span
                    data-slot="column-type"
                    className="shrink-0 rounded-full border border-white/20 px-2 py-0.5 text-xs text-white/70"
                  >
                    {columnTypeLabels[column.type]}
                  </span>
                </div>
                <ul
                  data-slot="column-samples"
                  className="mt-1.5 space-y-0.5 text-xs text-white/55"
                >
                  {(column.samples.length > 0
                    ? column.samples
                    : [LL.campaignImport.mapping.emptyValue()]
                  ).map((sample, i) => (
                    <li key={i} className="truncate">
                      {sample}
                    </li>
                  ))}
                </ul>
              </motion.li>
            ))}
          </ul>
        </section>

        <div className="relative hidden lg:col-start-2 lg:row-start-1 lg:block">
          <svg
            aria-hidden
            className="pointer-events-none absolute left-0 top-0"
            width={gutter.width}
            height={gutter.height}
            data-slot="connections"
          >
            {connections.map(({ field, path }, position) => (
              <motion.path
                key={field}
                d={path}
                data-connection={field}
                fill="none"
                stroke="#155DFC"
                strokeWidth="2"
                strokeLinecap="round"
                initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  delay: reduceMotion ? 0 : 0.25 + position * STAGGER_SECONDS,
                  duration: reduceMotion ? 0 : 0.5,
                }}
              />
            ))}
          </svg>
        </div>

        <section
          aria-labelledby="mapping-fields-title"
          className="space-y-3 lg:col-start-3 lg:row-start-1"
        >
          <h3
            id="mapping-fields-title"
            className="text-sm font-semibold text-white/85"
          >
            {LL.campaignImport.mapping.fieldsTitle()}
          </h3>
          <ul className="space-y-2">
            {IMPORT_FIELDS.map((field, position) => {
              const status = statusOf(field);
              const suggestion = state.suggestions.find(
                (item) => item.field === field
              );
              const column = state.mapping[field];
              return (
                <motion.li
                  key={field}
                  ref={(node: HTMLLIElement | null) => {
                    if (node) fieldRefs.current.set(field, node);
                    else fieldRefs.current.delete(field);
                  }}
                  data-field={field}
                  data-status={status}
                  initial={reduceMotion ? false : { opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: reduceMotion ? 0 : position * STAGGER_SECONDS,
                  }}
                  className={cn(
                    'rounded-lg border bg-white/[0.04] px-3.5 py-2.5 transition-colors duration-300',
                    status === 'missing'
                      ? 'border-amber-300/50'
                      : 'border-white/10'
                  )}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      {fieldLabels[field]}
                      <span className="ml-2 text-xs font-normal text-white/50">
                        {isRequired(field)
                          ? LL.campaignImport.mapping.required()
                          : LL.campaignImport.mapping.optional()}
                      </span>
                    </span>
                    <span
                      data-slot="field-status"
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-xs',
                        STATUS_STYLES[status]
                      )}
                    >
                      {statusLabels[status]}
                      {status === 'suggested' && suggestion
                        ? ` · ${
                            suggestion.confidence === 'high'
                              ? LL.campaignImport.mapping.confidenceHigh()
                              : LL.campaignImport.mapping.confidenceMedium()
                          }`
                        : ''}
                    </span>
                  </div>
                  <Select
                    value={column === undefined ? NO_COLUMN : String(column)}
                    onValueChange={(value) =>
                      dispatch({
                        type: 'mappingChanged',
                        field,
                        column: value === NO_COLUMN ? null : Number(value),
                      })
                    }
                  >
                    <SelectTrigger
                      aria-label={LL.campaignImport.mapping.columnAriaLabel({
                        field: fieldLabels[field],
                      })}
                      className="border-white/20 bg-white/5 text-white"
                    >
                      <SelectValue
                        placeholder={LL.campaignImport.mapping.columnPlaceholder()}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_COLUMN}>
                        {LL.campaignImport.mapping.noColumn()}
                      </SelectItem>
                      {columns.map((item) => (
                        <SelectItem key={item.index} value={String(item.index)}>
                          {item.header}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.li>
              );
            })}
          </ul>
        </section>
      </div>

      <div className="grid gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="import-date-format" className="text-white/85">
            {LL.campaignImport.mapping.dateFormatLabel()}
          </Label>
          <Select
            value={state.dateFormat}
            onValueChange={(value) => {
              const format = formatOptions.find((option) => option === value);
              if (format) {
                dispatch({
                  type: 'dateFormatChanged',
                  format,
                  now: DateTime.now(),
                });
              }
            }}
          >
            <SelectTrigger
              id="import-date-format"
              className="border-white/20 bg-white/5 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {formatOptions.map((format) => (
                <SelectItem key={format} value={format}>
                  {format === 'iso'
                    ? LL.campaignImport.mapping.dateFormatIso()
                    : format}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TimezoneCombobox
          timezone={state.timezone}
          onChange={(timezone) =>
            dispatch({ type: 'timezoneChanged', timezone, now: DateTime.now() })
          }
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div
          role="status"
          aria-live="polite"
          className="min-h-5 text-sm text-amber-200"
        >
          {missingFields.length > 0
            ? LL.campaignImport.mapping.missingFields({
                fields: missingFields
                  .map((field) => fieldLabels[field])
                  .join(', '),
              })
            : null}
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch({ type: 'goBack' })}
            className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            {LL.campaignImport.mapping.back()}
          </Button>
          <Button
            type="button"
            disabled={blockers.length > 0}
            onClick={() =>
              dispatch({ type: 'confirmMapping', now: DateTime.now() })
            }
            className="bg-[#155DFC] text-white hover:bg-[#155DFC]/90"
          >
            {LL.campaignImport.mapping.continue()}
          </Button>
        </div>
      </div>
    </div>
  );
}
